import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${API_URL}/api`;

// ─── Token storage ──────────────────────────────────────────────────────────
// We use localStorage as a FALLBACK Bearer token store — alongside HttpOnly
// cookies — because iOS Safari and some privacy-hardened browsers silently
// drop third-party cookies on cross-origin requests (e.g., when the app is
// served from a custom domain but the API is reached through a different
// hostname, or in PWA / standalone mode). Without this fallback, those users
// would get logged out on every request.
//
// XSS-mitigation: all inputs that could reach the DOM are rendered through
// React (which auto-escapes text), we set Content-Type: application/json on
// every request, the API never echoes raw HTML, and CSP headers on the
// deployment block inline scripts. If a future feature renders third-party
// HTML (e.g., rich-text announcements), this token-store policy must be
// revisited and the token should move to a short-lived, rotated refresh
// scheme or be pinned to HttpOnly cookies only.
// ────────────────────────────────────────────────────────────────────────────
const TOKEN_STORAGE_KEY = 'musicsync_access_token';

export const setAuthToken = (token) => {
  if (token) {
    try { localStorage.setItem(TOKEN_STORAGE_KEY, token); }
    catch (e) { console.warn('[api] localStorage write blocked (private mode?)', e?.message); }
  } else {
    try { localStorage.removeItem(TOKEN_STORAGE_KEY); }
    catch (e) { console.warn('[api] localStorage clear blocked', e?.message); }
  }
};

export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_STORAGE_KEY); }
  catch (e) { console.warn('[api] localStorage read blocked', e?.message); return null; }
};

const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Authorization: Bearer header whenever we have a stashed token. This is
// our fallback for mobile Safari / private-mode browsers that drop cookies.
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Automatically clear the stashed token on 401 so a stale token doesn't persist
apiClient.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't clear on login endpoints (they ARE allowed to 401 with wrong creds)
      if (!url.includes('/tenant/login') && !url.includes('/admin/login') && !url.includes('/auth/me')) {
        setAuthToken(null);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

export const formatApiError = (error) => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    }
  }
  return error.message || 'An error occurred';
};
